/**
 * Did the action the player just submitted actually reach the table?
 *
 * The problem this answers. `get_playermove` travels the legacy Socket.IO action
 * socket, and the engine acks it BEFORE it validates anything
 * (apps/engine/messages/inbound.js - `callback()` is the first statement in the
 * handler, ahead of the enqueue and every check). So a Socket.IO ack proves only
 * that a packet reached a socket worker's event loop. It does not mean the move
 * was applied, or even that it was kept. The engine then has roughly two dozen
 * bare `return` rejection paths with no log and no reply
 * (games/texas/playerMove.js - not seated, not your turn, arrived late, ...), so
 * there is no negative signal on the wire either.
 *
 * The client therefore cannot ask "was my action accepted?". What it CAN do is
 * watch for the action's EFFECT: when a move is applied, the table state moves.
 * The engine stamps `lastmove` on every applied action and on every other turn
 * advance (blinds, street change, timeout fold, timeout check, new hand - see
 * `handleAction` and the shared `updateTable*` queries), so a turn that has
 * moved on ALWAYS carries a different `lastmove`, whether or not the client saw
 * the frames in between. If the identity below is unchanged while the client
 * still believes it is our turn, the action did not land - whatever the ack
 * said.
 *
 * Why five fields and not just `lastmove`. `lastmove` is the load-bearing one:
 * `hand:move:playerAction` alone could not tell two turns apart when the client
 * missed the frame between them and the engine had written the same action
 * value twice ('call' over 'call' - it only clears `p{seat}action` on a street
 * transition). That gap would have re-sent a previous turn's action into a new
 * turn. `lastmove` is integer seconds and a seat's next turn starts at least
 * `speed` seconds after its last, so two turns of the same seat cannot share
 * it. The other fields stay for the one write that does not stamp `lastmove`
 * (`handleAction` with a non-empty `opt`, which no client sends) and so the
 * identity reads meaningfully in logs. `gameNo` makes hands distinct outright.
 *
 * Pure + framework-free so it is unit-testable without a store or a socket.
 */

/** The turn state captured at the moment an action was submitted. */
export type TurnSnapshot = {
  /** `game.gameNo` - the hand counter. */
  gameNo: unknown;
  /** `game.hand` - the street/stage code. */
  hand: number;
  /** `game.move` - the seat the engine says is to act. */
  move: unknown;
  /** `p{seat}action` - the engine's record of this seat's action. */
  playerAction: unknown;
  /** `game.lastmove` - stamped by the engine on every turn advance. */
  lastmove: unknown;
  /**
   * `game.bet` - the street's highest bet. Part of the action's turn stamp, not
   * of `turnIdentity` (see turnStamp).
   */
  bet?: unknown;
};

/**
 * A stable identity for "which turn is this".
 *
 * Used instead of an `isMyTurn` rising edge. The edge is only observable if the
 * client actually receives the intervening frame where it goes false, and on a
 * constrained link (or across back-to-back turns) that frame can be coalesced
 * away - leaving `isMyTurn` reading true, then true, so an edge-triggered effect
 * never re-runs. An identity is level-triggered: it differs across the two turns
 * whether or not the frame between them ever arrived.
 */
export const turnIdentity = (snapshot: TurnSnapshot): string =>
  [
    String(snapshot.gameNo),
    String(snapshot.hand),
    String(snapshot.move),
    String(snapshot.playerAction),
    String(snapshot.lastmove)
  ].join(':');

/**
 * True when the table state is the same turn state we submitted into - nothing
 * the engine would have changed if it had applied our move.
 */
export const turnStateUnchanged = (a: TurnSnapshot, b: TurnSnapshot): boolean =>
  turnIdentity(a) === turnIdentity(b);

/**
 * The turn an action was made on, appended to its `get_playermove` data so the
 * engine can discard it once that turn is over (EN-11171). A held action is
 * re-sent with the stamp of the turn it was COMMITTED on, so a copy that
 * outlives that turn - re-sent past a timeout, or delayed in transit - is
 * refused instead of being applied to the seat's next turn.
 *
 * `gameNo` + `hand` (street) + `bet` identify one turn of one seat: action
 * only returns to a seat on the same street after a bet or raise, so `bet` is
 * higher on its next turn. `lastmove` is not used because tournament seating
 * and maintenance resume re-stamp it while the same seat stays on the move.
 *
 * Empty when the frame lacks any of the three fields: the engine treats an
 * unstamped move as it did before, which is safer than a stamp that can never
 * match and would refuse every action.
 */
export const turnStamp = ({ gameNo, hand, bet }: TurnSnapshot): string => {
  const missing = (value: unknown) =>
    value === undefined || value === null || value === '' || Number.isNaN(value);
  if ([gameNo, hand, bet].some(missing)) return '';
  return `&gameNo=${gameNo}&hand=${hand}&bet=${bet}`;
};

/**
 * Whether the submitted action appears to have been dropped.
 *
 * `stillMyTurn` is required, and is the guard that keeps this honest: if the turn
 * has left us then something moved, and any conclusion about our own action is
 * moot. Only when the client still believes the table is waiting on us AND
 * nothing about the turn has changed do we call it dropped.
 */
export const actionAppearsDropped = (
  submitted: TurnSnapshot,
  current: TurnSnapshot,
  stillMyTurn: boolean
): boolean => stillMyTurn && turnStateUnchanged(submitted, current);

/**
 * Once the turn HAS moved on, did the action we were holding land, or was the
 * player acted for by the clock?
 *
 * Decided from the engine's own record of this seat's action in the first frame
 * of the new turn state. It is deliberately conservative: it only ever says
 * `lost` when the record is a different action that the clock would have taken
 * for the player (a fold, or a check where they meant to bet), because that is
 * the case the player must be told about - money left on the table without
 * their say. Anything ambiguous (an empty record on a new hand, an all-in
 * recorded for a call) is `unknown`, and no message is raised for it.
 *
 * Two `unknown` cases were queried in review as possible under-reporting.
 * Widening `lost` for either would tell players they were folded when they were
 * not:
 *
 * - **`raise` recorded as `call`.** Kept as `unknown` conservatively. The engine
 *   used to produce it by rewriting a `bet` sent into a live bet as a CALL (the
 *   `hasActiveBet` branch of apps/engine/games/texas/actions.js), but since
 *   EN-10927 that branch rejects the move instead of rewriting it. A `call`
 *   record is still not something the clock takes for the player (it checks or
 *   folds), so it is not evidence of a lost action.
 * - **`call` recorded as `check`.** For the engine to record a check for this
 *   seat there must have been nothing to call: the CALL branch early-returns on
 *   `callamount == 0`, and the timeout auto-check only fires when `checkBets` is
 *   satisfiable. Had there been a bet outstanding the clock would have folded
 *   the seat, not checked it - which this function already reports as `lost`.
 */
export type PendingActionOutcome = 'landed' | 'lost' | 'unknown';

const AGGRESSIVE_ACTIONS = ['raise', 'bet', 'allin'];

export const pendingActionOutcome = (
  sentAction: string,
  recordedAction: unknown
): PendingActionOutcome => {
  const sent = String(sentAction || '').toLowerCase();
  const recorded = String(recordedAction ?? '').toLowerCase();
  if (!sent || !recorded) return 'unknown';
  if (recorded === sent) return 'landed';
  if (recorded === 'fold' && sent !== 'fold') return 'lost';
  if (recorded === 'check' && AGGRESSIVE_ACTIONS.includes(sent)) return 'lost';
  return 'unknown';
};
